// /src/components/chat/shared/InputContainer.js
import React, { useState } from 'react';
import { Box, IconButton, Stack, styled, InputBase } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { trackChatActivity } from '../../../services/chatSessionService';

// Styled component to match the original ChatPage.js
const StyledInputContainer = styled(Stack)(({ backgroundColor }) => ({
  padding: '0', // Removed padding to let the inner pill handle spacing
  borderTop: '1px solid #E8E8E8',
  backgroundColor: backgroundColor,
}));

const InputContainer = ({ customization, handleSend, loading }) => {
  const [input, setInput] = useState('');
  const [isInputFocused, setIsInputFocused] = useState(false);

  const onSubmit = () => {
    // Don't send if loading or input is empty
    if (input.trim() !== '' && !loading) {
      trackChatActivity(); // Reset idle timer on send
      handleSend(input);
      setInput('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Only send if not loading and input is not empty
      if (!loading && input.trim()) {
        onSubmit();
      }
    }
  };

  return (
    <StyledInputContainer
      direction="column"
      backgroundColor={customization.footerBackgroundColor}
    >
      <Box sx={{
        background: '#ffffff',
        borderRadius: '25px',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        border: isInputFocused ? '1px solid var(--primary-color, #1976d2)' : '1px solid #cfcfcf',
        boxShadow: isInputFocused ? '0 0 0 2px color-mix(in srgb, var(--primary-color, #1976d2) 20%, transparent)' : 'none',
        minHeight: 50,
        margin: '10px',
        paddingRight: '4px', // Space for the button
      }}>
        <InputBase
          fullWidth
          multiline
          maxRows={4}
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            trackChatActivity(); // Reset idle timer on typing
          }}
          onKeyDown={handleKeyPress}
          onFocus={() => setIsInputFocused(true)}
          onBlur={() => setIsInputFocused(false)}
          placeholder={customization.inputPlaceholder || 'Type your message...'}
          sx={{
            flex: 1,
            padding: '8px 16px',
            background: 'transparent',
            fontSize: customization.fontSize === 'small' ? '12px' : customization.fontSize === 'large' ? '16px' : '14px',
            color: '#222',
            '& textarea': {
              resize: 'none',
              overflowY: 'auto',
              scrollbarWidth: 'none',         // Firefox
              '-ms-overflow-style': 'none',   // IE 10+
              maxHeight: '160px',             // safety cap (~4 lines)
              lineHeight: 1.4,
            },
            '& textarea::-webkit-scrollbar': { display: 'none' }, // WebKit
          }}
        />
        <IconButton
          onClick={onSubmit}
          disabled={loading || !input.trim()}
          sx={{
            width: 44,
            height: 44,
            borderRadius: 0,
            color: (loading || !input.trim()) ?
              customization.sendIconDisabledColor || 'rgba(0, 0, 0, 0.26)' :
              customization.sendIconColor || 'var(--primary-color, #1976d2)',
            '&.Mui-disabled': {
              color: customization.sendIconDisabledColor || 'rgba(0, 0, 0, 0.26)'
            }
          }}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </StyledInputContainer>
  );
};

export default InputContainer;
